import { useRecoilValue } from "recoil"
import { useModalitiesProvider } from "../../../../../../../Providers/ModalitiesProvider"
import { useEnvironmentVariablesProvider } from "../../../../../../../Providers/EnvironmentVariablesProvider"
import { currentPatientFileInfoAtom } from "../../../Atoms/PatientFile"
import { DataSource } from "../../../Types/DataSource"
import { getSupportedRenderStrategies, TraceConfigSeed, TraceOption } from "../../../Types/Trace"

const modalityProvider = (modality?: { file_name?: string }): string => {
	const parts = modality?.file_name?.split(",") ?? []
	return parts.length > 2 ? parts[parts.length - 2] : ""
}

const optionKey = (value: TraceConfigSeed): string => `${value.dataSource}:${value.rawDataKey}`

export const useSourceTraceOptions = (configuredTraces: TraceConfigSeed[]): TraceOption[] => {
	const { sourceModalities } = useRecoilValue(currentPatientFileInfoAtom)
	const { modalities } = useModalitiesProvider()
	const { environmentVariables } = useEnvironmentVariablesProvider()
	const options = new Map<string, TraceOption>()
	const availableModalities = new Set(sourceModalities.get(DataSource.REVIEW_TIMESERIES) ?? [])

	Object.entries(modalities)
		.filter(([name]) => !name.startsWith("EEG"))
		.forEach(([name, modality]) => {
			const value: TraceConfigSeed = {
				name,
				dataKey: name,
				rawDataKey: name,
				dataSource: DataSource.REVIEW_TIMESERIES,
				units: "",
				isCompositePart: false,
			}
			options.set(optionKey(value), {
				label: name,
				source: modalityProvider(modality),
				value,
				supportedRenderStrategies: getSupportedRenderStrategies(value, { environmentVariables, fileName: modality?.file_name }),
				highlight: availableModalities.has(name),
			})
		})

	configuredTraces.forEach(trace => {
		const normalizedTrace = {
			...trace,
			dataSource: DataSource.REVIEW_TIMESERIES,
		}
		const key = optionKey(normalizedTrace)
		if (options.has(key)) return

		const isAvailable = availableModalities.has(trace.rawDataKey)
		const provider = modalityProvider(modalities[trace.rawDataKey]) || "Unavailable"
		options.set(key, {
			label: trace.rawDataKey,
			source: provider,
			value: normalizedTrace,
			supportedRenderStrategies: getSupportedRenderStrategies(normalizedTrace, { environmentVariables, fileName: modalities[trace.rawDataKey]?.file_name }),
			highlight: isAvailable,
		})
	})

	return Array.from(options.values()).sort((first, second) => {
		if (first.highlight !== second.highlight) return first.highlight ? -1 : 1
		return first.label.localeCompare(second.label)
	})
}
